/**
 * DirectReportCard Component
 * Card showing a direct report's performance summary
 */

import React from 'react';
import { clsx } from 'clsx';
import { Card } from '../../common';
import { User, TrendingUp, TrendingDown, Minus, ChevronRight } from 'lucide-react';
import { getScoreColor, getScoreCategory } from '../../../types';

export interface DirectReportCardProps {
  userId: string;
  userName: string;
  userEmail?: string;
  avatarUrl?: string;
  score: number;
  previousScore?: number;
  okrCount: number;
  okrsCompleted: number;
  onClick?: (userId: string) => void;
  className?: string;
  isLoading?: boolean;
}

const CATEGORY_LABELS = {
  excellent: 'Excellent',
  good: 'Good',
  needsImprovement: 'Needs Improvement',
  poor: 'Poor',
};

export const DirectReportCard: React.FC<DirectReportCardProps> = ({
  userId,
  userName,
  userEmail,
  avatarUrl,
  score,
  previousScore,
  okrCount,
  okrsCompleted,
  onClick,
  className,
  isLoading = false,
}) => {
  const category = getScoreCategory(score);
  const scoreColor = getScoreColor(score);

  // Calculate trend
  const trend = previousScore !== undefined ? score - previousScore : 0;
  const trendDirection = trend > 0 ? 'up' : trend < 0 ? 'down' : 'neutral';

  if (isLoading) {
    return (
      <Card className={clsx('p-4', className)}>
        <div className="animate-pulse flex items-center gap-4">
          <div className="w-12 h-12 bg-stone-200 rounded-full" />
          <div className="flex-1">
            <div className="h-4 bg-stone-200 rounded w-32 mb-2" />
            <div className="h-3 bg-stone-200 rounded w-24" />
          </div>
          <div className="h-8 w-16 bg-stone-200 rounded" />
        </div>
      </Card>
    );
  }

  return (
    <Card
      className={clsx(
        'p-4 transition-all',
        onClick && 'hover:shadow-md cursor-pointer hover:border-blue-200',
        className
      )}
      onClick={() => onClick?.(userId)}
    >
      <div className="flex items-center gap-4">
        {/* Avatar */}
        <div className="flex-shrink-0">
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt={userName}
              className="w-12 h-12 rounded-full object-cover"
            />
          ) : (
            <div className="w-12 h-12 rounded-full bg-stone-100 flex items-center justify-center">
              <User className="w-6 h-6 text-stone-400" />
            </div>
          )}
        </div>

        {/* User Info */}
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-stone-800 truncate">{userName}</h4>
          {userEmail && (
            <p className="text-sm text-stone-500 truncate">{userEmail}</p>
          )}
          <div className="flex items-center gap-3 mt-1">
            <span className="text-xs text-stone-400">
              {okrsCompleted}/{okrCount} OKRs completed
            </span>
            <span
              className={clsx(
                'text-xs px-2 py-0.5 rounded-full',
                category === 'excellent' && 'bg-green-100 text-green-700',
                category === 'good' && 'bg-blue-100 text-blue-700',
                category === 'needsImprovement' && 'bg-amber-100 text-amber-700',
                category === 'poor' && 'bg-red-100 text-red-700'
              )}
            >
              {CATEGORY_LABELS[category]}
            </span>
          </div>
        </div>

        {/* Score */}
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="flex items-center gap-1">
              <span
                className="text-2xl font-bold"
                style={{ color: scoreColor }}
              >
                {Math.round(score)}
              </span>
              {trendDirection === 'up' && (
                <TrendingUp className="w-4 h-4 text-green-500" />
              )}
              {trendDirection === 'down' && (
                <TrendingDown className="w-4 h-4 text-red-500" />
              )}
              {trendDirection === 'neutral' && previousScore !== undefined && (
                <Minus className="w-4 h-4 text-stone-400" />
              )}
            </div>
            {previousScore !== undefined && trend !== 0 && (
              <span
                className={clsx(
                  'text-xs',
                  trend > 0 ? 'text-green-600' : 'text-red-600'
                )}
              >
                {trend > 0 ? '+' : ''}{trend.toFixed(1)} pts
              </span>
            )}
          </div>
          {onClick && (
            <ChevronRight className="w-5 h-5 text-stone-400" />
          )}
        </div>
      </div>
    </Card>
  );
};

export default DirectReportCard;
